import { InfoMessage } from '@/components/shared/InfoMessage/InfoMessage';
import { Button } from '@/components/ui/Button/Button';
import { DialogBody, DialogFooter } from '@/components/ui/Dialog/Dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/Form/Form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/RadioGroup/RadioGroup';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select/Select';
import { toast } from '@/components/ui/Toast/Toast';
import { useTimeFor } from '@/hooks/useTimeFor';
import { cn } from '@/utils';
import { isAfter, subHours } from 'date-fns';
import { useRef } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RecurringCalendar } from '../../../RecurringCalendar/RecurringCalendar';
import type { StudentsSessionDetails } from '../../../ScheduleDialog/context/ScheduleFlowContext';
import { SchedulingCalendar } from '../../../SchedulingCalendar/SchedulingCalendar';
import { SessionSummary } from '../../../SessionSummary/SessionSummary';
import { useDateAndTimeStep } from './DateAndTimeStep.hooks';

interface DateAndTimeStepProps {
  portalContainerRef?: React.RefObject<HTMLElement | null>;
}

const WARNING_WINDOW_HOURS = 24;

export const DateAndTimeStep = ({ portalContainerRef }: DateAndTimeStepProps) => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'rescheduleDialog.steps.dateAndTimeStep' });
  const { t: tScheduleStep } = useTranslation('scheduling', { keyPrefix: 'scheduleDialog.steps.dateAndTimeStep' });

  const { zoned } = useTimeFor('sessionTime');
  const { formatWithZone, formatDate } = useTimeFor('cancellationDeadline');

  const dateAndTimeErrorRef = useRef<HTMLParagraphElement>(null);
  const recurringCalendarErrorRef = useRef<HTMLParagraphElement>(null);

  const {
    form,
    onSubmit,
    data,
    showRecurringOptions,
    repeatOptions,
    endDateOptions,
    isCustomEndDate,
    closeRescheduleDialog,
    appointmentEnrollment,
    freeCancellationUntil,
    appointmentStudent,
    centerDetails,
    maxRecurringDate,
    isReschedulingLoading,
  } = useDateAndTimeStep();

  if (!appointmentStudent || !appointmentEnrollment || !data.appointment) {
    throw new Error('Appointment data not found');
  }

  const studentsConfig = data?.config?.data?.students ?? [];

  const studentConfig = data?.config?.data?.students.find((student) => student.id === appointmentStudent?.id);

  if (!studentConfig) {
    throw new Error('Student config not found for the appointment student');
  }

  if (!centerDetails) {
    throw new Error('Center details not found for the appointment');
  }

  if (centerDetails.recurring_enabled && !maxRecurringDate) {
    throw new Error('Missing max recurring date');
  }

  if (!freeCancellationUntil) {
    throw new Error('Free cancellation date not found');
  }

  const studentSessionDetails: StudentsSessionDetails = {
    studentId: appointmentStudent.id,
    enrollmentId: appointmentEnrollment.id,
    enrollmentType: appointmentEnrollment.type,
    enrollmentEndDate: appointmentEnrollment.enrollmentEndDate,
    centerDetails: centerDetails,
    deliveryType: data.appointment.delivery_type,
    durationInMinutes: data.appointment.duration_minutes,
    sessionType: data.appointment.session_type_enum,
  };

  const warningStart = subHours(freeCancellationUntil, WARNING_WINDOW_HOURS);
  const shouldShowWarningBanner = isAfter(new Date(), warningStart);

  const selectedDate = form.getValues('newDateAndTime');

  const handleFieldErrors = (
    errors: FieldErrors<{
      newDateAndTime: string;
      applyToFollowingSessions: boolean;
      recurrencePattern?: string;
      endDate?: string;
      customEndDate?: string;
    }>,
  ) => {
    if (errors.newDateAndTime) {
      toast({
        variant: 'destructive',
        description: t('validationError.noDateSelected'),
      });
      dateAndTimeErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    if (errors.applyToFollowingSessions || errors.recurrencePattern || errors.endDate) {
      return;
    }

    if (errors.customEndDate) {
      toast({
        variant: 'destructive',
        description: t('validationError.customEndDate'),
      });
      recurringCalendarErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }
  };

  return (
    <>
      <DialogBody>
        {shouldShowWarningBanner && (
          <InfoMessage variant='warning' title={t('warning.title')}>
            <p className='text-body-m-regular'>
              {t('warning.description.beforeDate')}{' '}
              <time dateTime={freeCancellationUntil.toISOString()}>
                <strong>{formatDate(freeCancellationUntil)}</strong> {t('warning.description.at')} <strong>{formatWithZone(freeCancellationUntil, 'p')}</strong>
              </time>
              {t('warning.description.afterDate')}
            </p>
          </InfoMessage>
        )}
        <SessionSummary appointment={data.appointment} enrollment={appointmentEnrollment} />
        <h2 className='text-heading-s-medium pt-10 pb-6'>{t('title')}</h2>
        <Form {...form}>
          <form
            id='date-and-time-form'
            onSubmit={(e) =>
              void form.handleSubmit(onSubmit, (errors) => {
                handleFieldErrors(errors);
              })(e)
            }
            className='flex flex-1 flex-col space-y-4 pb-0'
          >
            <FormField
              control={form.control}
              name='newDateAndTime'
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <SchedulingCalendar
                      value={field.value}
                      onChange={field.onChange}
                      students={appointmentStudent ? [appointmentStudent] : []}
                      studentsConfig={studentsConfig}
                      studentSessionDetails={[studentSessionDetails]}
                      reschedulingAppointmentId={data.appointment?.appointment_id}
                      isRecurring={!!data.appointment?.recurring_schedule}
                    />
                  </FormControl>
                  <div ref={dateAndTimeErrorRef} className='mt-4'>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />

            {data.appointment?.recurring_schedule && selectedDate && (
              <div className='mt-6 space-y-4 pb-6'>
                <FormField
                  control={form.control}
                  name='applyToFollowingSessions'
                  render={({ field }) => (
                    <FormItem className='space-y-3'>
                      <FormLabel className='text-base font-medium'>{t('applyChangeTo')}</FormLabel>
                      <FormControl>
                        <RadioGroup
                          value={field.value ? 'thisAndFollowing' : 'thisSession'}
                          onValueChange={(value) => field.onChange(value === 'thisAndFollowing')}
                          className='flex flex-col space-y-2'
                        >
                          <div className='bg-fill-background-light mb-1 flex flex-row-reverse items-center justify-between rounded-lg px-5 py-3'>
                            <RadioGroupItem value='thisSession' id='thisSession' />
                            <label htmlFor='thisSession' className='text-body-m-semibold'>
                              {t('thisSession')}
                            </label>
                          </div>
                          <div className='bg-fill-background-light mb-1 flex flex-row-reverse items-center justify-between rounded-lg px-5 py-3'>
                            <RadioGroupItem value='thisAndFollowing' id='thisAndFollowing' />
                            <label htmlFor='thisAndFollowing' className='text-body-m-semibold'>
                              {t('thisAndFollowingSessions')}
                            </label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                    </FormItem>
                  )}
                />

                {showRecurringOptions && (
                  <div className='space-y-4 pt-2'>
                    <FormField
                      control={form.control}
                      name='recurrencePattern'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel
                            className={cn('text-text-default-secondary', {
                              'text-text-error-primary': !!form.formState.errors.recurrencePattern,
                            })}
                          >
                            {tScheduleStep('repeatLabel')}
                          </FormLabel>
                          <FormControl>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger ref={field.ref} isError={!!form.formState.errors.recurrencePattern}>
                                <SelectValue placeholder={tScheduleStep('selectPlaceholder')} />
                              </SelectTrigger>
                              <SelectContent portalContainer={portalContainerRef?.current}>
                                {repeatOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='endDate'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel
                            className={cn('text-text-default-secondary', {
                              'text-text-error-primary': !!form.formState.errors.endDate,
                            })}
                          >
                            {tScheduleStep('endDateLabel')}
                          </FormLabel>
                          <FormControl>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger ref={field.ref} isError={!!form.formState.errors.endDate}>
                                <SelectValue placeholder={tScheduleStep('selectPlaceholder')} />
                              </SelectTrigger>
                              <SelectContent portalContainer={portalContainerRef?.current} side={isCustomEndDate ? 'bottom' : 'top'}>
                                {endDateOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {isCustomEndDate && (
                      <FormField
                        control={form.control}
                        name='customEndDate'
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <RecurringCalendar
                                value={field.value}
                                onChange={field.onChange}
                                maxRecurringDate={maxRecurringDate!}
                                firstSessionDate={zoned(selectedDate)}
                              />
                            </FormControl>
                            <div ref={recurringCalendarErrorRef}>
                              <FormMessage className='mt-3' />
                            </div>
                          </FormItem>
                        )}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </form>
        </Form>
      </DialogBody>
      <DialogFooter>
        <Button type='button' variant='text-secondary' onClick={closeRescheduleDialog} className='flex-1'>
          {t('cancelButton.label')}
        </Button>
        <Button type='submit' form='date-and-time-form' className='flex-1' isLoading={isReschedulingLoading} disabled={isReschedulingLoading}>
          {t('continueButton.label')}
        </Button>
      </DialogFooter>
    </>
  );
};
